import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Plus } from 'lucide-react';
import {
  ContactType,
  ReceiptNature,
  ElectionDesignation,
  InKindNature,
  PaymentMethod,
  getGates,
  requiresInKindNature,
} from '@signalvote/shared';
import { FederalTransferQualification, normalizeTransferQualification } from '@signalvote/shared';
import type { CreateContributionRequest, CreateFinanceContactRequest, RuleOutcomeDto } from '@signalvote/shared';
import { ComplianceOutcomesView, InlineContactForm, ContributionFormFields } from './entryParts';
import { ContactCombobox } from '@/components/finance/shared/classification';
import { useCreateContribution, useValidateDraftContribution } from '@/hooks/queries/useFinanceContributions';
import { useCreateFinanceContact } from '@/hooks/queries/useFinanceContacts';
import { useDonorPickerContacts } from '@/hooks/queries/useDonorPickerContacts';
import { useFinanceSettings, useComplianceProfile } from '@/hooks/queries/useFinanceSettings';
import { useElectionDesignationOptions, electionOptionKey } from '@/hooks/useElectionDesignationOptions';
import { getCampaignTodayString } from '@/lib/calendarUtils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface AddContributionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * BSW-004: optional link to a raw bank line. Plumbed through to the create
   * mutation so callers that open this dialog from a bank-row context can
   * persist the link in one step. The BSW-007+ classification panels are the
   * primary surface for this; the standalone Add Contribution dialog passes
   * null by default.
   */
  bankTransactionId?: string | null;
}

const CREATE_NEW = '__create_new__';

export function AddContributionDialog({ open, onOpenChange, bankTransactionId = null }: AddContributionDialogProps) {
  const { mutateAsync: createContribution, isPending } = useCreateContribution();
  const { mutateAsync: validateDraft } = useValidateDraftContribution();
  const { mutateAsync: createContact, isPending: isCreatingContact } = useCreateFinanceContact();
  const { data: settings } = useFinanceSettings();
  const { data: profile } = useComplianceProfile();

  const [contactId, setContactId] = useState('');
  const { contacts, prospects, onSearchChange: onContactSearchChange } = useDonorPickerContacts(contactId);
  const [selectedContactName, setSelectedContactName] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(() => getCampaignTodayString());
  const [contributionType, setContributionType] = useState<ReceiptNature>(ReceiptNature.Cash);
  const [notes, setNotes] = useState('');
  const [inKindFairMarketValueBasis, setInKindFairMarketValueBasis] = useState('');
  const [inKindDescription, setInKindDescription] = useState('');
  const [inKindNature, setInKindNature] = useState<InKindNature | null>(null);
  // No default: preselecting Check would let a currency gift slip past the cash cap unasked, and
  // preselecting Cash would claim currency the treasurer never said. Required for monetary receipts.
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | null>(null);
  // #711 — starts at None: an unanswered transfer must meter like any other committee gift.
  // Silence buying the exemption is exactly the bug this closes.
  const [transferQualification, setTransferQualification] =
    useState<FederalTransferQualification>(FederalTransferQualification.None);
  const [showMoreTypes, setShowMoreTypes] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Inline contact creation toggle (form state lives inside InlineContactForm)
  const [creatingInline, setCreatingInline] = useState(false);

  // #144 — the shared election options (own cycle + next cycle on federal; today's plain list on
  // AL/VA), consumed by both this picker and the bank-classify picker via one hook so they can't
  // drift. Auto-select the single election when there's exactly one; else default Undesignated.
  const electionOptions = useElectionDesignationOptions(settings, profile);

  const defaultDesignation = useMemo(() => {
    const electionCount = (settings?.primaryDate ? 1 : 0) + (settings?.generalDate ? 1 : 0);
    if (electionCount === 1) {
      return settings?.primaryDate ? ElectionDesignation.Primary : ElectionDesignation.General;
    }
    return ElectionDesignation.Undesignated;
  }, [settings?.primaryDate, settings?.generalDate]);

  const [electionDesignation, setElectionDesignation] = useState<ElectionDesignation>(defaultDesignation);
  // The off-cycle year, or null for the campaign's own current cycle. The picker selection sets
  // both this and the designation; the create request carries it (null on own cycle / Undesignated).
  const [electionYear, setElectionYear] = useState<number | null>(null);

  // Compliance validation state
  const [complianceOutcomes, setComplianceOutcomes] = useState<RuleOutcomeDto[]>([]);
  // ADR-040 (§24.2-954) — reason the treasurer gives to knowingly proceed past an overridable gate.
  const [overrideReason, setOverrideReason] = useState('');
  const validateTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // An in-kind gift isn't paid, so it carries no payment method.
  const sentPaymentMethod = contributionType === ReceiptNature.InKind ? null : paymentMethod;

  const triggerValidation = useCallback(() => {
    clearTimeout(validateTimerRef.current);

    const parsed = parseFloat(amount);
    if (isNaN(parsed) || parsed <= 0) {
      setComplianceOutcomes([]);
      return;
    }

    validateTimerRef.current = setTimeout(async () => {
      try {
        const result = await validateDraft({
          amount: parsed,
          date,
          contributionType,
          electionDesignation,
          contactId: contactId || null,
          // The cash cap keys on currency, so the live check needs how the gift was paid.
          paymentMethod: sentPaymentMethod,
          // #711 — so the live gate judges the same answer a save would store: a transfer with
          // no qualifying relationship shows its over-limit warning while the treasurer types.
          transferQualification: normalizeTransferQualification(contributionType, transferQualification),
        });
        setComplianceOutcomes(result.outcomes);
      } catch {
        // Silently ignore validation errors — don't block the form
      }
    }, 300);
  }, [amount, date, contributionType, electionDesignation, contactId, sentPaymentMethod, transferQualification, validateDraft]);

  useEffect(() => {
    triggerValidation();
    return () => clearTimeout(validateTimerRef.current);
  }, [triggerValidation]);

  const parsedAmount = parseFloat(amount);
  const inKindRequired = contributionType === ReceiptNature.InKind;
  // AL requires an in-kind nature before an in-kind contribution can file (the AL
  // builder throws MissingInKindNature without it); every other state ignores the
  // value. Route through the shared `requiresInKindNature` predicate — the one source
  // of truth for this AL-only axis — so the nature picker hides + the save gate lifts
  // off-AL, instead of an inline `stateCode === 'AL'` re-derivation.
  const isAlabama = profile?.stateCode === 'AL';
  const inKindNatureRequired = inKindRequired && requiresInKindNature(profile?.stateCode);
  // ADR-040 (§24.2-954) — an overridable compliance gate (e.g. the VA legislative-session ban)
  // requires an explicit reason before the treasurer can proceed; the reason is audited server-side.
  const requiresOverrideReason = getGates(complianceOutcomes).some((g) => g.overrideAllowed);
  // A NON-overridable gate is a legal PROHIBITION (NC corporate/union source § 163-278.15; cash
  // over the § 163-278.14(b) cap) — there is no lawful override, so it permanently dead-ends Save,
  // mirroring the bank-classify GateStack (no Override button ⇒ "Classify with Overrides" stays
  // disabled). The red gate alert is the explanation; the server also hard-rejects the create.
  const hasNonOverridableGate = getGates(complianceOutcomes).some((g) => !g.overrideAllowed);
  const canSave =
    contactId !== '' &&
    !isNaN(parsedAmount) &&
    parsedAmount > 0 &&
    (!inKindRequired || inKindFairMarketValueBasis.trim() !== '') &&
    (!inKindRequired || inKindDescription.trim() !== '') &&
    (!inKindNatureRequired || inKindNature !== null) &&
    (inKindRequired || paymentMethod !== null) &&
    (!requiresOverrideReason || overrideReason.trim() !== '') &&
    !hasNonOverridableGate &&
    !isPending;

  // Build contribution type options with progressive disclosure. ADR-057 (#344) — the
  // person/organization gift members were retired; a plain monetary gift is Cash and the
  // contributor's person-vs-org identity lives on the contact's ContactKind/OrganizationType.
  const baseTypeOptions: { value: ReceiptNature; label: string }[] = [
    { value: ReceiptNature.Cash, label: 'Cash' },
    { value: ReceiptNature.InKind, label: 'In-Kind' },
  ];

  const moreTypeOptions: { value: ReceiptNature; label: string }[] = [];
  // #711 — the jurisdiction's DECLARED capability. This was gated on `federalTransferLimit !=
  // null`, a field the serve path never populated, so the option never rendered for anyone; and
  // that field could not have been the gate anyway, since Alabama declares a federal-transfer
  // limit ($1,000) and federal declares none.
  if (profile?.offersFederalTransferReceipt === true) {
    moreTypeOptions.push({ value: ReceiptNature.FederalTransfer, label: 'Federal Transfer' });
  }

  const typeOptions = showMoreTypes ? [...baseTypeOptions, ...moreTypeOptions] : baseTypeOptions;

  const showEmployerOccupation = profile?.employerOccupationRequired ?? false;

  const resetForm = () => {
    setContactId('');
    setSelectedContactName('');
    setAmount('');
    setDate(getCampaignTodayString());
    setContributionType(ReceiptNature.Cash);
    setTransferQualification(FederalTransferQualification.None);
    const elCount = (settings?.primaryDate ? 1 : 0) + (settings?.generalDate ? 1 : 0);
    if (elCount === 1) {
      setElectionDesignation(settings?.primaryDate ? ElectionDesignation.Primary : ElectionDesignation.General);
    } else {
      setElectionDesignation(ElectionDesignation.Undesignated);
    }
    // Reset to the campaign's own cycle (an off-cycle designation is a deliberate per-gift pick).
    setElectionYear(null);
    setNotes('');
    setInKindFairMarketValueBasis('');
    setInKindDescription('');
    setInKindNature(null);
    setPaymentMethod(null);
    setShowMoreTypes(false);
    setError(null);
    setComplianceOutcomes([]);
    setOverrideReason('');
    setCreatingInline(false);
  };

  const handleContactChange = (value: string) => {
    if (value === CREATE_NEW) {
      setCreatingInline(true);
    } else {
      setContactId(value);
      const contact = contacts.find((c) => c.id === value) ?? prospects.find((c) => c.id === value);
      setSelectedContactName(contact?.name ?? '');
    }
  };

  const handleInlineContactSave = async (request: CreateFinanceContactRequest) => {
    try {
      const result = await createContact(request);
      setContactId(result.contact.id);
      setSelectedContactName(result.contact.name);
      setCreatingInline(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create contact');
    }
  };

  const handleSave = async () => {
    if (!canSave) return;
    setError(null);

    const request: CreateContributionRequest = {
      contactId,
      amount: parsedAmount,
      date,
      contributionType,
      electionDesignation,
      // #144 — the off-cycle year (null for own cycle); the server normalizes it away on Undesignated.
      electionYear,
      notes: notes.trim() || null,
      inKindFairMarketValueBasis:
        contributionType === ReceiptNature.InKind
          ? inKindFairMarketValueBasis.trim() || null
          : null,
      inKindDescription:
        contributionType === ReceiptNature.InKind
          ? inKindDescription.trim() || null
          : null,
      inKindNature:
        contributionType === ReceiptNature.InKind ? inKindNature : null,
      paymentMethod: sentPaymentMethod,
      // #711 — the recorded qualifying relationship; null on every other receipt, where the
      // question decides nothing. The shared normalizer mirrors the server's, so no client can
      // send an unanswered transfer and mint a still-exempt legacy row.
      transferQualification: normalizeTransferQualification(contributionType, transferQualification),
      bankTransactionId,
      // ADR-040 (§24.2-954) — carry the audited override reason only when an overridable gate is
      // actually showing, so the server records a knowing acceptance (US#6).
      overrideReason: requiresOverrideReason ? overrideReason.trim() : null,
    };

    try {
      await createContribution(request);
      resetForm();
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create contribution');
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) resetForm();
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md" data-testid="add-contribution-dialog">
        <DialogHeader>
          <DialogTitle>Add Contribution</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {/* Contact selection or inline creation */}
          {creatingInline ? (
            <InlineContactForm
              contactType={ContactType.Donor}
              showEmployerOccupation={showEmployerOccupation}
              supportedOrganizationTypes={profile?.supportedOrganizationTypes ?? []}
              isSaving={isCreatingContact}
              onSave={handleInlineContactSave}
              onCancel={() => setCreatingInline(false)}
            />
          ) : contactId && selectedContactName ? (
            <div>
              <label className="block text-sm font-medium mb-1">Contact *</label>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{selectedContactName}</span>
                <button
                  type="button"
                  onClick={() => { setContactId(''); setSelectedContactName(''); }}
                  className="text-xs text-primary hover:underline"
                >
                  Change
                </button>
              </div>
            </div>
          ) : (
            <div>
              <label htmlFor="contrib-contact" className="block text-sm font-medium mb-1">Contact *</label>
              <ContactCombobox
                id="contrib-contact"
                value={contactId}
                onChange={handleContactChange}
                contacts={contacts}
                prospects={prospects}
                onSearchChange={onContactSearchChange}
                placeholder="Select a contact..."
              />
              <button
                type="button"
                onClick={() => handleContactChange(CREATE_NEW)}
                className="mt-1 flex items-center gap-1 text-xs text-primary underline"
              >
                <Plus className="h-3 w-3" />
                Create new contact
              </button>
            </div>
          )}

          <ContributionFormFields
            amount={amount}
            onAmountChange={setAmount}
            date={date}
            onDateChange={setDate}
            contributionType={contributionType}
            onContributionTypeChange={setContributionType}
            typeOptions={typeOptions}
            showMoreTypesButton={!showMoreTypes && moreTypeOptions.length > 0}
            onShowMoreTypes={() => setShowMoreTypes(true)}
            inKindFairMarketValueBasis={inKindFairMarketValueBasis}
            onInKindFmvChange={setInKindFairMarketValueBasis}
            inKindDescription={inKindDescription}
            onInKindDescriptionChange={setInKindDescription}
            inKindNature={inKindNature}
            onInKindNatureChange={setInKindNature}
            inKindNatureRequired={inKindNatureRequired}
            transferQualification={transferQualification}
            onTransferQualificationChange={setTransferQualification}
            paymentMethod={paymentMethod}
            onPaymentMethodChange={setPaymentMethod}
            showAlabamaReceiptDateRule={isAlabama}
            electionOptions={electionOptions}
            selectedElectionKey={electionOptionKey(electionDesignation, electionYear)}
            onElectionChange={(designation, year) => {
              setElectionDesignation(designation);
              setElectionYear(year);
            }}
            notes={notes}
            onNotesChange={setNotes}
          />

          <ComplianceOutcomesView outcomes={complianceOutcomes} />

          {requiresOverrideReason && (
            <div className="space-y-1">
              <label htmlFor="contrib-override-reason" className="block text-sm font-medium text-red-800">
                Reason to record it anyway *
              </label>
              <p className="text-xs text-muted-foreground">
                This will be saved to the audit log as a knowing override.
              </p>
              <textarea
                id="contrib-override-reason"
                data-testid="override-reason-input"
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                rows={2}
                className="w-full rounded-md border border-red-200 bg-white px-3 py-2 text-sm focus:border-red-400 focus:outline-none"
                placeholder="e.g. Candidate's own personal funds — not yet attributed to the candidate contact"
              />
            </div>
          )}

          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={() => handleOpenChange(false)}
            className="rounded-md px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-accent"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? 'Saving...' : 'Save'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
